import { AfterViewInit, Component, ElementRef, EventEmitter, OnDestroy,
         Output, ViewChild
       } from '@angular/core';

import { DesignStepService
       } from 'collage-designer/design-step/design-step.service';
import { PlaceholderService
       } from 'collage-designer/stage/placeholder/placeholder.service';
import { StageService } from 'collage-designer/stage/stage.service';
import { ImageStoreService } from 'core/imagestore/imagestore.service';
import { PrnglService } from 'core/prngl/prngl.service';
import { Utils } from 'core/utils';
import { AsyncEventEmitterSubscriber } from 'core/utils/async-event-emitter';
import { StageUtils } from 'core/utils.stage';
import { Subscription } from 'rxjs';

@Component({
    selector: 'prngl-preview',
    template: `
    <div class="wrapper">
      <div #spinner class="spinner">
        <mat-spinner color="accent"></mat-spinner>
      </div>
      <img #image alt="" (error)="respondToImageFailure()">
    </div>
  `,
    styleUrls: ['./prngl-preview.component.scss'],
    standalone: false
})
export class PrnglPreviewComponent implements AfterViewInit, OnDestroy
{
  private static readonly PREVIEW_TYPE: string = 'box';
  private static readonly UPDATE_DELAY: number = 500; /* ms */

  @Output() public previewFailed: EventEmitter<void> = new EventEmitter();

  @ViewChild('image') public image: ElementRef<HTMLImageElement>;
  @ViewChild('spinner') public spinner: ElementRef<HTMLElement>;

  private designStepId: number;
  private revision: number = 0;
  private isDestroyed: boolean = false;

  private subscriptions: Subscription = new Subscription();
  private stageConfigAppliedSubscription:
      AsyncEventEmitterSubscriber<boolean>;

  private scheduleUpdate: () => void = Utils.debounce(
      () => this.update(), PrnglPreviewComponent.UPDATE_DELAY);

  constructor(
      private stageService: StageService,
      private designStepService: DesignStepService,
      private placeholderService: PlaceholderService,
      private imageStoreService: ImageStoreService,
      private prnglService: PrnglService,
  )
  {
    this.designStepId = this.designStepService.getDesignStepId();

    this.stageConfigAppliedSubscription =
        this.stageService.stageConfigApplied.subscribe(
            async () => this.scheduleUpdate());
    this.subscriptions.add(
        this.placeholderService.placeholderChanged.subscribe(() => {
          if (this.stageService.getIsStageLoaded()) {
            this.scheduleUpdate();
          }
        }));
  }

  public ngAfterViewInit(): void
  {
    if (this.stageService.getIsStageLoaded()) {
      this.update();
    }
  }

  public ngOnDestroy(): void
  {
    this.isDestroyed = true;
    this.subscriptions.unsubscribe();
    this.stageService.stageConfigApplied.unsubscribe(
        this.stageConfigAppliedSubscription);
  }

  public respondToImageFailure(): void
  {
    if (this.isDestroyed) {
      return;
    }

    this.hideSpinner();
    this.previewFailed.emit();
  }

  private async update(): Promise<void>
  {
    if (this.isDestroyed ||
        this.designStepService.getDesignStepId() !== this.designStepId) {
      return;
    }

    const stage = this.stageService.getCurrentStageReference();
    if (!stage) {
      return;
    }

    const revision = ++this.revision;
    this.showSpinner();

    try {
      const structureUrl = await this.uploadStructure(<any>stage.node);
      if (this.isDestroyed || revision !== this.revision) {
        return;
      }

      const request = this.prnglService.makeRenderRequest(
          this.designStepService.getProductId(),
          PrnglPreviewComponent.PREVIEW_TYPE);
      request.fillDesignedParts();
      request.addDesignedPart(
          this.designStepService.getPart().id, structureUrl);
      request.viewportLongSide = this.prnglService.snapToViewportStep(
          this.longSide);

      const image = this.image.nativeElement;
      image.onload = () => {
        if (revision === this.revision) {
          this.hideSpinner();
        }
      };
      image.src = request.url;
    }
    catch (exc) {
      console.warn('PRNGL preview could not be updated', exc);
      this.respondToImageFailure();
    }
  }

  private async uploadStructure(stage: SVGElement): Promise<string>
  {
    const svg = <SVGElement>stage.cloneNode(true);
    const originalViewBox = svg.getAttribute('pap:original-viewBox');
    if (originalViewBox) {
      svg.setAttribute('viewBox', originalViewBox);
    }

    const serializedSvg = StageUtils.withoutNonBreakingSpaces(
        new XMLSerializer().serializeToString(svg));
    const info = await this.imageStoreService.postSvg(
        StageUtils.sanitizeStructure(serializedSvg));

    return info.location;
  }

  private get longSide(): number
  {
    const wrapper = this.image.nativeElement.parentElement;
    return Math.max(wrapper.offsetWidth, wrapper.offsetHeight)
        * (window.devicePixelRatio || 1);
  }

  private showSpinner(): void
  {
    this.spinner.nativeElement.style.display = 'flex';
    this.image.nativeElement.style.opacity = '0.5';
  }

  private hideSpinner(): void
  {
    this.spinner.nativeElement.style.display = 'none';
    this.image.nativeElement.style.opacity = '1';
  }
}
